import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import {
  ERPPage, ERPPageHeader, ERPCard, ERPButton, ERPLoading, ERPError
} from '../../components/ui/ERPLayout';
import { supabase } from '../../lib/supabase';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const inputCls = 'w-full bg-[#111111] border border-white/10 text-white/85 text-sm rounded-lg px-3 py-2.5 font-oranienbaum placeholder-white/25 focus:outline-none focus:border-[#D6EF4E]/40 transition-colors';
const labelCls = 'block text-xs font-oranienbaum text-white/50 mb-1.5 uppercase tracking-wider';

const LAS_DOS_DONCELLAS = 'las dos doncellas';

const isLDD = (nombre) => nombre?.toLowerCase()?.includes(LAS_DOS_DONCELLAS);

// Calcular si la trazabilidad está completa
const calcularEstadoTrazabilidad = (row) => {
  const campos = {
    producto: !!(row?.producto_sala_nombre || row?.producto_tienda_nombre),
    cliente: !!row?.cliente_nombre,
    fecha: !!(row?.fecha_produccion || row?.fecha_registro),
    loteInterno: !!row?.lote_interno,
    loteExterno: !!row?.lote_externo,
    pieza: !!(row?.peso_inicial && parseFloat(row?.peso_inicial) > 0),
  };

  const esLDD = isLDD(row?.cliente_nombre || '');
  if (esLDD) {
    campos.blisteres = row?.numero_blisteres != null && parseInt(row?.numero_blisteres) > 0;
  }

  const faltantes = Object.entries(campos)
    .filter(([, v]) => !v)
    .map(([k]) => {
      const labels = {
        producto: 'Producto',
        cliente: 'Cliente',
        fecha: 'Fecha',
        loteInterno: 'Lote Interno',
        loteExterno: 'Lote Externo',
        pieza: 'Peso inicial',
        blisteres: 'Nº Blísteres',
      };
      return labels[k] || k;
    });

  return {
    completa: faltantes.length === 0,
    faltantes,
    porcentaje: Math.round(((Object.keys(campos).length - faltantes.length) / Object.keys(campos).length) * 100),
  };
};

// ─── Badge de estado ─────────────────────────────────────────────────────────
const TrazabilidadBadge = ({ row }) => {
  const estado = calcularEstadoTrazabilidad(row);
  if (estado.completa) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-oranienbaum font-semibold"
        style={{ background: 'rgba(58,127,107,0.15)', color: '#3A7F6B', border: '1px solid rgba(58,127,107,0.30)' }}>
        <Icon name="CheckCircle2" size={11} />
        Completa
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-oranienbaum font-semibold"
      style={{ background: 'rgba(201,162,77,0.12)', color: '#C9A24D', border: '1px solid rgba(201,162,77,0.25)' }}>
      <Icon name="AlertTriangle" size={11} />
      Incompleta
    </span>
  );
};

// ─── Línea temporal ──────────────────────────────────────────────────────────
const TimelineStep = ({ icon, label, sublabel, active, last }) => (
  <div className="flex items-start gap-3">
    <div className="flex flex-col items-center">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${active
        ? 'bg-[#D6EF4E]/20 border border-[#D6EF4E]/50'
        : 'bg-white/5 border border-white/10'}`}>
        <Icon name={icon} size={14} style={{ color: active ? '#D6EF4E' : 'rgba(255,255,255,0.25)' }} />
      </div>
      {!last && (
        <div className="w-px flex-1 mt-1" style={{ minHeight: '24px', background: active ? 'rgba(214,239,78,0.25)' : 'rgba(255,255,255,0.08)' }} />
      )}
    </div>
    <div className="pb-5 min-w-0">
      <p className={`text-sm font-oranienbaum font-semibold ${active ? 'text-white/90' : 'text-white/30'}`}>{label}</p>
      {sublabel && <p className="text-xs font-oranienbaum text-white/40 mt-0.5 truncate">{sublabel}</p>}
    </div>
  </div>
);

// ─── Panel de detalle ─────────────────────────────────────────────────────────
const DetallePanel = ({ row, onClose }) => {
  if (!row) return null;
  const estado = calcularEstadoTrazabilidad(row);
  const esLDD = isLDD(row?.cliente_nombre || '');

  const timelineSteps = [
    {
      icon: 'ShoppingBag',
      label: 'Compra',
      sublabel: row?.numero_factura
        ? `Factura ${row.numero_factura} — ${row?.proveedor_nombre || 'Proveedor desconocido'}`
        : row?.proveedor_nombre || null,
      active: !!(row?.numero_factura || row?.proveedor_nombre),
    },
    {
      icon: 'Package',
      label: 'Entrada del Lote',
      sublabel: row?.fecha_entrada
        ? `${new Date(row.fecha_entrada).toLocaleDateString('es-ES')} — ${row?.lote_proveedor || row?.numero_lote_tienda || 'Sin nº lote'}`
        : row?.numero_lote_tienda || null,
      active: !!(row?.lote_tienda_id || row?.fecha_entrada),
    },
    {
      icon: 'FileText',
      label: 'Registro de Pieza',
      sublabel: row?.fecha_registro
        ? new Date(row.fecha_registro).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : null,
      active: !!row?.loncheado_id,
    },
    {
      icon: 'Scissors',
      label: 'Loncheado',
      sublabel: row?.empleado_nombre
        ? `Cortador: ${row.empleado_nombre}${row?.rendimiento ? ` — Rendimiento: ${parseFloat(row.rendimiento).toFixed(1)}%` : ''}`
        : null,
      active: !!(row?.peso_loncheado || row?.empleado_nombre),
    },
    {
      icon: 'Box',
      label: 'Producción de Blísteres',
      sublabel: esLDD && row?.numero_blisteres != null
        ? `${row.numero_blisteres} blísteres`
        : esLDD ? 'Sin registrar' : 'N/A para este cliente',
      active: esLDD ? (row?.numero_blisteres != null && parseInt(row.numero_blisteres) > 0) : true,
    },
    {
      icon: 'Users',
      label: 'Cliente',
      sublabel: row?.cliente_nombre || null,
      active: !!row?.cliente_nombre,
      last: true,
    },
  ];

  const fotoUrls = (() => {
    if (!row?.foto_url) return [];
    try {
      const parsed = JSON.parse(row.foto_url);
      return Array.isArray(parsed) ? parsed : [row.foto_url];
    } catch {
      return [row.foto_url];
    }
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={onClose}>
      <div
        className="w-full max-w-4xl max-h-[95vh] overflow-y-auto rounded-2xl"
        style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.10)' }}
        onClick={e => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 sticky top-0 z-10"
          style={{ background: '#141414', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div>
            <h2 className="text-xl font-bold font-oranienbaum" style={{ color: '#D6EF4E' }}>
              Detalle de Trazabilidad
            </h2>
            <p className="text-xs font-oranienbaum text-white/40 mt-0.5">
              {row?.lote_externo || `ID: ${row?.loncheado_id?.substring(0, 8)}…`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <TrazabilidadBadge row={row} />
            <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors" style={{ color: 'rgba(255,255,255,0.5)' }}>
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Columna izquierda: Línea temporal */}
          <div className="lg:col-span-1">
            <ERPCard title="Cadena de Trazabilidad" titleAction={null}>
              <div className="pt-1">
                {timelineSteps.map((step, i) => (
                  <TimelineStep key={i} {...step} last={step.last || i === timelineSteps.length - 1} />
                ))}
              </div>
            </ERPCard>

            {/* Estado de completitud */}
            <ERPCard title="Estado de Validación" titleAction={null}>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-oranienbaum text-white/70">Completitud</span>
                  <span className="text-sm font-bold font-oranienbaum" style={{ color: estado.completa ? '#3A7F6B' : '#C9A24D' }}>
                    {estado.porcentaje}%
                  </span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <div className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${estado.porcentaje}%`, background: estado.completa ? '#3A7F6B' : '#C9A24D' }} />
                </div>
                {estado.faltantes.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs font-oranienbaum text-white/40 uppercase tracking-wider">Información faltante:</p>
                    {estado.faltantes.map((f, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Icon name="AlertCircle" size={11} style={{ color: '#C9A24D' }} />
                        <span className="text-xs font-oranienbaum text-white/60">{f}</span>
                      </div>
                    ))}
                  </div>
                )}
                {estado.completa && (
                  <div className="flex items-center gap-2 mt-1">
                    <Icon name="CheckCircle2" size={13} style={{ color: '#3A7F6B' }} />
                    <span className="text-xs font-oranienbaum" style={{ color: '#3A7F6B' }}>Trazabilidad completa</span>
                  </div>
                )}
              </div>
            </ERPCard>
          </div>

          {/* Columna central y derecha: Datos */}
          <div className="lg:col-span-2 space-y-4">
            {/* Lotes */}
            <ERPCard title="Identificadores de Lote" titleAction={null}>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg p-3" style={{ background: 'rgba(214,239,78,0.06)', border: '1px solid rgba(214,239,78,0.15)' }}>
                  <p className="text-xs font-oranienbaum text-white/40 uppercase tracking-wider mb-1">Lote Externo</p>
                  <p className="text-base font-bold font-oranienbaum" style={{ color: '#D6EF4E' }}>
                    {row?.lote_externo || '—'}
                  </p>
                </div>
                <div className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <p className="text-xs font-oranienbaum text-white/40 uppercase tracking-wider mb-1">Lote Interno</p>
                  <p className="text-base font-bold font-oranienbaum text-white/85">
                    {row?.lote_interno || '—'}
                  </p>
                </div>
                {row?.lote_proveedor && (
                  <div className="rounded-lg p-3 col-span-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <p className="text-xs font-oranienbaum text-white/40 uppercase tracking-wider mb-1">Lote Proveedor</p>
                    <p className="text-sm font-oranienbaum text-white/85">{row.lote_proveedor}</p>
                  </div>
                )}
                {esLDD && (
                  <div className="rounded-lg p-3" style={{ background: 'rgba(201,162,77,0.08)', border: '1px solid rgba(201,162,77,0.20)' }}>
                    <p className="text-xs font-oranienbaum text-white/40 uppercase tracking-wider mb-1">Nº Blísteres</p>
                    <p className="text-base font-bold font-oranienbaum" style={{ color: '#C9A24D' }}>
                      {row?.numero_blisteres ?? '—'}
                    </p>
                  </div>
                )}
              </div>
            </ERPCard>

            {/* Pieza y rendimiento */}
            <ERPCard title="Datos de la Pieza" titleAction={null}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Peso Inicial', value: row?.peso_inicial ? `${parseFloat(row.peso_inicial).toFixed(2)} kg` : '—', color: 'text-white/85' },
                  { label: 'Peso Loncheado', value: row?.peso_loncheado ? `${parseFloat(row.peso_loncheado).toFixed(2)} kg` : '—', color: 'text-white/85' },
                  { label: 'Merma', value: row?.merma ? `${parseFloat(row.merma).toFixed(2)} kg` : '—', color: 'text-[#E05252]' },
                  { label: 'Rendimiento', value: row?.rendimiento ? `${parseFloat(row.rendimiento).toFixed(1)}%` : '—', color: parseFloat(row?.rendimiento) >= 70 ? 'text-[#3A7F6B]' : 'text-[#C9A24D]' },
                ].map((item, i) => (
                  <div key={i} className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="text-xs font-oranienbaum text-white/40 mb-1">{item.label}</p>
                    <p className={`text-sm font-bold font-oranienbaum ${item.color}`}>{item.value}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <p className="text-xs font-oranienbaum text-white/40 mb-1">Producto</p>
                  <p className="text-sm font-oranienbaum text-white/85">{row?.producto_sala_nombre || row?.producto_tienda_nombre || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-oranienbaum text-white/40 mb-1">Tipo de Corte</p>
                  <p className="text-sm font-oranienbaum text-white/85">{row?.tipo_corte || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-oranienbaum text-white/40 mb-1">Cortador</p>
                  <p className="text-sm font-oranienbaum text-white/85">{row?.empleado_nombre || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-oranienbaum text-white/40 mb-1">Fecha de Registro</p>
                  <p className="text-sm font-oranienbaum text-white/85">
                    {row?.fecha_registro ? new Date(row.fecha_registro).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                  </p>
                </div>
              </div>
              {row?.observaciones && (
                <div className="mt-3 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="text-xs font-oranienbaum text-white/40 mb-1">Observaciones</p>
                  <p className="text-sm font-oranienbaum text-white/70">{row.observaciones}</p>
                </div>
              )}
            </ERPCard>

            {/* Proveedor y factura */}
            {(row?.proveedor_nombre || row?.numero_factura) && (
              <ERPCard title="Proveedor y Factura de Compra" titleAction={null}>
                <div className="grid grid-cols-2 gap-3">
                  {row?.proveedor_nombre && (
                    <>
                      <div>
                        <p className="text-xs font-oranienbaum text-white/40 mb-1">Proveedor</p>
                        <p className="text-sm font-oranienbaum text-white/85">{row.proveedor_nombre}</p>
                      </div>
                      {row?.proveedor_nif && (
                        <div>
                          <p className="text-xs font-oranienbaum text-white/40 mb-1">NIF/CIF</p>
                          <p className="text-sm font-oranienbaum text-white/85">{row.proveedor_nif}</p>
                        </div>
                      )}
                    </>
                  )}
                  {row?.numero_factura && (
                    <>
                      <div>
                        <p className="text-xs font-oranienbaum text-white/40 mb-1">Nº Factura</p>
                        <p className="text-sm font-oranienbaum text-white/85">{row.numero_factura}</p>
                      </div>
                      <div>
                        <p className="text-xs font-oranienbaum text-white/40 mb-1">Fecha Factura</p>
                        <p className="text-sm font-oranienbaum text-white/85">
                          {row?.fecha_factura ? new Date(row.fecha_factura).toLocaleDateString('es-ES') : '—'}
                        </p>
                      </div>
                      {row?.factura_total != null && (
                        <div>
                          <p className="text-xs font-oranienbaum text-white/40 mb-1">Total Factura</p>
                          <p className="text-sm font-oranienbaum text-white/85">{parseFloat(row.factura_total).toFixed(2)} €</p>
                        </div>
                      )}
                    </>
                  )}
                  {row?.fecha_entrada && (
                    <div>
                      <p className="text-xs font-oranienbaum text-white/40 mb-1">Fecha de Entrada</p>
                      <p className="text-sm font-oranienbaum text-white/85">{new Date(row.fecha_entrada).toLocaleDateString('es-ES')}</p>
                    </div>
                  )}
                </div>
              </ERPCard>
            )}

            {/* Fotografías */}
            {fotoUrls.length > 0 && (
              <ERPCard title="Fotografías" titleAction={null}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {fotoUrls.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                      className="block rounded-lg overflow-hidden aspect-square hover:opacity-80 transition-opacity"
                      style={{ border: '1px solid rgba(255,255,255,0.10)' }}>
                      <img src={url} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              </ERPCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Página principal ─────────────────────────────────────────────────────────
const Trazabilidad = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Filtros
  const [filtros, setFiltros] = useState({
    loteExterno: '',
    loteInterno: '',
    cliente: '',
    producto: '',
    empleado: '',
    fechaDesde: '',
    fechaHasta: '',
    validada: '',
  });

  // Listas para filtros
  const [clientes, setClientes] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [productos, setProductos] = useState([]);

  const loadFiltros = useCallback(async () => {
    try {
      const [{ data: c }, { data: e }, { data: p }] = await Promise.all([
        supabase.from('clientes').select('id, nombre').eq('activo', true).order('nombre'),
        supabase.from('empleados').select('id, nombre').eq('activo', true).order('nombre'),
        supabase.from('productos').select('id, nombre').eq('activo', true).order('nombre'),
      ]);
      setClientes(c || []);
      setEmpleados(e || []);
      setProductos(p || []);
    } catch { /* silent */ }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('loncheados')
        .select(`
          id,
          lote_externo,
          lote_interno,
          numero_blisteres,
          fecha_produccion,
          trazabilidad_validada,
          created_at,
          peso_inicial,
          peso_loncheado,
          peso_aprovechado,
          merma,
          rendimiento,
          estado_rendimiento,
          foto_url,
          observaciones,
          tipo,
          lote_id,
          clientes:cliente_id ( id, nombre ),
          empleados:empleado_id ( id, nombre ),
          productos:producto_id ( id, nombre )
        `)
        .order('created_at', { ascending: false })
        .limit(200);

      if (filtros.loteExterno) query = query.ilike('lote_externo', `%${filtros.loteExterno}%`);
      if (filtros.loteInterno) query = query.ilike('lote_interno', `%${filtros.loteInterno}%`);
      if (filtros.cliente) query = query.eq('cliente_id', filtros.cliente);
      if (filtros.empleado) query = query.eq('empleado_id', filtros.empleado);
      if (filtros.producto) query = query.eq('producto_id', filtros.producto);
      if (filtros.fechaDesde) query = query.gte('created_at', filtros.fechaDesde);
      if (filtros.fechaHasta) query = query.lte('created_at', filtros.fechaHasta + 'T23:59:59');
      if (filtros.validada === 'true') query = query.eq('trazabilidad_validada', true);
      if (filtros.validada === 'false') query = query.eq('trazabilidad_validada', false);

      const { data: rows, error: err } = await query;
      if (err) throw err;

      // Normalizar estructura
      const normalized = (rows || []).map(r => ({
        loncheado_id: r.id,
        lote_externo: r.lote_externo,
        lote_interno: r.lote_interno,
        numero_blisteres: r.numero_blisteres,
        fecha_produccion: r.fecha_produccion,
        trazabilidad_validada: r.trazabilidad_validada,
        fecha_registro: r.created_at,
        peso_inicial: r.peso_inicial,
        peso_loncheado: r.peso_loncheado,
        peso_aprovechado: r.peso_aprovechado,
        merma: r.merma,
        rendimiento: r.rendimiento,
        estado_rendimiento: r.estado_rendimiento,
        foto_url: r.foto_url,
        observaciones: r.observaciones,
        tipo_corte: r.tipo,
        lote_id: r.lote_id,
        cliente_id: r.clientes?.id,
        cliente_nombre: r.clientes?.nombre,
        empleado_id: r.empleados?.id,
        empleado_nombre: r.empleados?.nombre,
        producto_sala_id: r.productos?.id,
        producto_sala_nombre: r.productos?.nombre,
      }));

      setData(normalized);
    } catch (err) {
      setError(err?.message || 'Error al cargar los datos de trazabilidad.');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    loadFiltros();
  }, [loadFiltros]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFiltro = (key, val) => {
    setFiltros(prev => ({ ...prev, [key]: val }));
  };

  const limpiarFiltros = () => {
    setFiltros({ loteExterno: '', loteInterno: '', cliente: '', producto: '', empleado: '', fechaDesde: '', fechaHasta: '', validada: '' });
  };

  const hayFiltros = Object.values(filtros).some(v => v !== '');

  // Estadísticas rápidas
  const stats = {
    total: data.length,
    completas: data.filter(r => calcularEstadoTrazabilidad(r).completa).length,
    incompletas: data.filter(r => !calcularEstadoTrazabilidad(r).completa).length,
    validadas: data.filter(r => r.trazabilidad_validada).length,
  };

  return (
    <>
      <Helmet>
        <title>Trazabilidad — Las Dos Doncellas</title>
      </Helmet>
      <ERPPage>
        <ERPPageHeader
          title="Trazabilidad"
          subtitle="Cadena completa: proveedor → lote → pieza → loncheado → blísteres → cliente"
          actions={
            <ERPButton variant="ghost" icon="RefreshCw" onClick={loadData} disabled={loading}>
              Actualizar
            </ERPButton>
          }
        />

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Total Registros', value: stats.total, color: '#D6EF4E', icon: 'Database' },
            { label: 'Trazabilidad Completa', value: stats.completas, color: '#3A7F6B', icon: 'CheckCircle2' },
            { label: 'Trazabilidad Incompleta', value: stats.incompletas, color: '#C9A24D', icon: 'AlertTriangle' },
            { label: 'Validadas', value: stats.validadas, color: '#8097D6', icon: 'ShieldCheck' },
          ].map((k, i) => (
            <div key={i} className="rounded-xl p-4" style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Icon name={k.icon} size={14} style={{ color: k.color }} />
                <p className="text-xs font-oranienbaum text-white/50">{k.label}</p>
              </div>
              <p className="text-2xl font-bold font-oranienbaum" style={{ color: k.color }}>
                {loading ? '—' : k.value}
              </p>
            </div>
          ))}
        </div>

        {/* Filtros */}
        <ERPCard title="Búsqueda y Filtros" titleAction={
          hayFiltros ? (
            <button onClick={limpiarFiltros} className="text-xs font-oranienbaum text-white/40 hover:text-white/70 flex items-center gap-1 transition-colors">
              <Icon name="X" size={11} /> Limpiar
            </button>
          ) : null
        }>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className={labelCls}>Lote Externo</label>
              <input type="text" value={filtros.loteExterno} onChange={e => handleFiltro('loteExterno', e.target.value)}
                placeholder="LDD-20260728-001" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Lote Interno</label>
              <input type="text" value={filtros.loteInterno} onChange={e => handleFiltro('loteInterno', e.target.value)}
                placeholder="LDD-3 / 690" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Cliente</label>
              <select value={filtros.cliente} onChange={e => handleFiltro('cliente', e.target.value)}
                className={inputCls} style={{ appearance: 'none' }}>
                <option value="">Todos los clientes</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Producto</label>
              <select value={filtros.producto} onChange={e => handleFiltro('producto', e.target.value)}
                className={inputCls} style={{ appearance: 'none' }}>
                <option value="">Todos los productos</option>
                {productos.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Empleado / Cortador</label>
              <select value={filtros.empleado} onChange={e => handleFiltro('empleado', e.target.value)}
                className={inputCls} style={{ appearance: 'none' }}>
                <option value="">Todos los empleados</option>
                {empleados.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Fecha Desde</label>
              <input type="date" value={filtros.fechaDesde} onChange={e => handleFiltro('fechaDesde', e.target.value)}
                className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Fecha Hasta</label>
              <input type="date" value={filtros.fechaHasta} onChange={e => handleFiltro('fechaHasta', e.target.value)}
                className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Estado de Validación</label>
              <select value={filtros.validada} onChange={e => handleFiltro('validada', e.target.value)}
                className={inputCls} style={{ appearance: 'none' }}>
                <option value="">Todos</option>
                <option value="true">Validada</option>
                <option value="false">Sin validar</option>
              </select>
            </div>
          </div>
        </ERPCard>

        {/* Tabla de resultados */}
        <ERPCard title={`Registros (${data.length})`} titleAction={null}>
          {loading ? (
            <ERPLoading message="Cargando trazabilidad..." />
          ) : error ? (
            <ERPError message={error} onRetry={loadData} />
          ) : data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <Icon name="Search" size={22} style={{ color: 'rgba(255,255,255,0.20)' }} />
              </div>
              <p className="font-oranienbaum text-white/40 text-sm">No se encontraron registros</p>
              {hayFiltros && (
                <button onClick={limpiarFiltros} className="mt-3 text-xs font-oranienbaum text-[#D6EF4E]/60 hover:text-[#D6EF4E] transition-colors">
                  Limpiar filtros
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-oranienbaum">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    {['Lote Externo', 'Lote Interno', 'Cliente', 'Producto', 'Cortador', 'Fecha', 'Rendimiento', 'Estado', 'Acciones'].map(h => (
                      <th key={h} className="text-left py-3 px-3 text-xs text-white/40 uppercase tracking-wider font-normal whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => {
                    const estado = calcularEstadoTrazabilidad(row);
                    return (
                      <tr key={row.loncheado_id}
                        className="transition-colors cursor-pointer hover:bg-white/3"
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                        onClick={() => setSelectedRow(row)}>
                        <td className="py-3 px-3">
                          <span className="font-semibold" style={{ color: '#D6EF4E' }}>
                            {row.lote_externo || <span className="text-white/25 italic">Sin asignar</span>}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-white/70">{row.lote_interno || '—'}</td>
                        <td className="py-3 px-3 text-white/85">{row.cliente_nombre || '—'}</td>
                        <td className="py-3 px-3 text-white/70 max-w-[140px] truncate">{row.producto_sala_nombre || '—'}</td>
                        <td className="py-3 px-3 text-white/70">{row.empleado_nombre || '—'}</td>
                        <td className="py-3 px-3 text-white/50 whitespace-nowrap">
                          {row.fecha_registro ? new Date(row.fecha_registro).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}
                        </td>
                        <td className="py-3 px-3">
                          {row.rendimiento ? (
                            <span className="font-semibold" style={{ color: parseFloat(row.rendimiento) >= 70 ? '#3A7F6B' : '#C9A24D' }}>
                              {parseFloat(row.rendimiento).toFixed(1)}%
                            </span>
                          ) : <span className="text-white/25">—</span>}
                        </td>
                        <td className="py-3 px-3">
                          <TrazabilidadBadge row={row} />
                        </td>
                        <td className="py-3 px-3">
                          <button
                            onClick={e => { e.stopPropagation(); setSelectedRow(row); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-colors hover:bg-white/8"
                            style={{ color: '#D6EF4E', border: '1px solid rgba(214,239,78,0.20)' }}>
                            <Icon name="Eye" size={12} />
                            Ver
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </ERPCard>
      </ERPPage>

      {/* Panel de detalle */}
      {selectedRow && (
        <DetallePanel row={selectedRow} onClose={() => setSelectedRow(null)} />
      )}
    </>
  );
};

export default Trazabilidad;
